import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Loader2, Mic, MonitorSpeaker, RotateCcw } from 'lucide-react';
import { captureService, type AudioSource, type CaptureAudioTrack, type CaptureTranscript, type CaptureTranscriptState } from '@/services/captureApi';
import { cn } from '@/lib/utils';

export interface TranscriptStep {
  stepId: string;
  title: string;
  screenshot: string | null;
}

const POLL_MS = 5000;

const TRACK_LABEL: Record<AudioSource, string> = { mic: 'Microphone', tab: 'Tab audio' };
const TRACK_ICON: Record<AudioSource, typeof Mic> = { mic: Mic, tab: MonitorSpeaker };

const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/** The Transcript tab: a player for each track, then each step's picture with what was said on the mic and the tab. */
export function TranscriptView({ captureId, tracks, steps }: { captureId: string; tracks: CaptureAudioTrack[]; steps: TranscriptStep[] }) {
  const [state, setState] = useState<CaptureTranscriptState | null>(null);
  const [round, setRound] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const players = useRef<Partial<Record<AudioSource, HTMLAudioElement | null>>>({});

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const next = await captureService.getTranscript(captureId);
        if (cancelled) return;
        setState(next);
        if (next.status === 'pending') timer = setTimeout(load, POLL_MS);
      } catch {
        // An API that can't say: the players are still here.
        if (!cancelled) setState({ status: 'not_transcribed' });
      }
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [captureId, round]);

  const retry = useCallback(async () => {
    setRetrying(true);
    try {
      setState(await captureService.retryTranscript(captureId));
      setRound(r => r + 1);
    } catch {
      setState({ status: 'failed', error: 'Could not ask for the transcription again.' });
    } finally {
      setRetrying(false);
    }
  }, [captureId]);

  const transcript = state?.status === 'ready' ? state.transcript : null;
  const offsets = Object.fromEntries((transcript?.tracks ?? []).map(t => [t.source, t.offset])) as Partial<Record<AudioSource, number>>;
  const seek = (at: number) => {
    for (const t of tracks) {
      const el = players.current[t.source];
      if (el) el.currentTime = Math.max(0, at - (offsets[t.source] ?? 0));
    }
    players.current.mic?.play().catch(() => {});
  };

  return (
    <div className="flex-1 overflow-y-auto" data-testid="transcript-view">
      <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
        <section className="grid gap-3 sm:grid-cols-2">
          {tracks.map(t => {
            const Icon = TRACK_ICON[t.source];
            return (
              <div key={t.source} className="rounded-xl border border-border bg-card p-3 space-y-2">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  {TRACK_LABEL[t.source]}
                  <span className="font-normal text-muted-foreground tabular-nums">{clock(t.durationS)}</span>
                </p>
                <audio
                  ref={el => { players.current[t.source] = el; }}
                  controls
                  preload="metadata"
                  src={t.url}
                  aria-label={TRACK_LABEL[t.source]}
                  className="w-full h-9"
                />
              </div>
            );
          })}
        </section>

        <TranscriptStatus state={state} retrying={retrying} onRetry={retry} />

        {transcript && <StepList transcript={transcript} steps={steps} tracks={tracks} onSeek={seek} />}
      </div>
    </div>
  );
}

function TranscriptStatus({ state, retrying, onRetry }: { state: CaptureTranscriptState | null; retrying: boolean; onRetry: () => void }) {
  if (!state || state.status === 'pending') {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
        Transcribing the audio…
      </p>
    );
  }
  if (state.status === 'ready') return null;
  const failed = state.status === 'failed';
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <p className={cn('flex items-center gap-2', failed ? 'text-destructive' : 'text-muted-foreground')}>
        {failed && <AlertCircle className="h-4 w-4" />}
        {failed ? "The audio couldn't be transcribed." : "The audio hasn't been transcribed."}
      </p>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        className="h-8 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 text-xs font-medium hover:bg-muted disabled:opacity-60"
      >
        <RotateCcw className={cn('h-3.5 w-3.5', retrying && 'animate-spin')} />
        Try again
      </button>
    </div>
  );
}

function StepList({ transcript, steps, tracks, onSeek }: {
  transcript: CaptureTranscript;
  steps: TranscriptStep[];
  tracks: CaptureAudioTrack[];
  onSeek: (at: number) => void;
}) {
  const byId = new Map(steps.map(s => [s.stepId, s]));
  const sources = tracks.map(t => t.source);
  return (
    <section className="space-y-3">
      {transcript.steps.map((s, i) => {
        const step = byId.get(s.step);
        return (
          <article key={`${s.step}-${i}`} data-testid="transcript-step" className="rounded-xl border border-border bg-card p-3 flex gap-4">
            <div className="w-48 shrink-0 aspect-video rounded-md bg-muted overflow-hidden">
              {step?.screenshot && <img src={step.screenshot} alt={step.title} className="w-full h-full object-cover object-top" />}
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <header className="flex items-baseline gap-2">
                <span className="text-[11px] font-mono font-semibold text-muted-foreground">{i + 1}</span>
                <h3 className="text-sm font-semibold text-foreground truncate">{step?.title || s.step}</h3>
                {s.start !== null && (
                  <button
                    type="button"
                    onClick={() => onSeek(s.start!)}
                    className="ml-auto text-[11px] tabular-nums text-primary hover:underline"
                    title="Play from this step"
                  >
                    {clock(s.start)}
                  </button>
                )}
              </header>
              {sources.map(source => {
                const Icon = TRACK_ICON[source];
                const said = s[source];
                return (
                  <p key={source} className="flex gap-2 text-sm" data-source={source}>
                    <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" aria-label={TRACK_LABEL[source]} />
                    <span className={cn(said ? 'text-foreground' : 'text-muted-foreground italic')}>{said || 'Nothing said'}</span>
                  </p>
                );
              })}
            </div>
          </article>
        );
      })}
    </section>
  );
}
